// frontend/src/farm/FarmAnimalLayer.js — farm animals inside FarmWorld's depth-sorted `objects`
// container. Since 4d the SERVER decides what every animal does (app/realtime/herds.py) and streams
// intentions; this layer only plays them (herdPlayback.mjs), so every viewer sees the same farm:
// the 4-frame walk mirrored for the other side, the idle pose, a product bubble, hidden inside
// the hut. Before the first snapshot an animal stands at its saved spot.
import { cellFToLocal, cellCentre, depthAt, TILE_H, localToCellF } from './iso.mjs';
import { depthAmongObjects } from './animalDepth.mjs';
import { frontCell } from './farmObjects.mjs';
import { farmAnimals, isHungry } from './farmAnimals.mjs';
import { homeCell, isNight, animalScale } from './animalRoam.mjs';
import { positionAt, clockOffset, intentForSize, fallbackIntent, restFrame } from './herdPlayback.mjs';
import { cycleT } from './skyClock.mjs';
import { clockInput } from './FarmBackdrop.js';
import { iconKey } from '../ui/icons.mjs';
import { audioEvents } from '../audio/audioEvents.js';
import { pickAnimal } from './animalPick.mjs';
import { serverNow } from '../state/serverClock.mjs';

const FRAME_MS = 160;
const BUBBLE = { kura: 'egg', krowa: 'milk' };
const BUBBLE_PX = 18;                              // the egg / milk over a ready animal (owner: 28 was too big)
const FOOD_PX = 24;                                // the hunger bubble: the food it wants (hay / grain)
const RING = { fill: 0xffffff, fillAlpha: 0.5, line: 0xffc233 };   // under the animal the pointer is on
const STAND_IN = { kura: [48, 43], krowa: [168, 96] };  // display size of the placeholder until the art lands (real proportions)

export class FarmAnimalLayer {
  constructor(scene, world) {
    this.scene = scene; this.world = world;
    this._items = new Map(); this._data = null;
    this._intents = new Map();                    // animal id → the server's latest intention
    this._offset = 0;                             // server clock − this device's clock (from the snapshot)
    this._streaming = false;                      // a snapshot arrived: the server drives them
  }

  // herd_snapshot replaces everything (and sets the clock offset); herd merges by id. Each intention
  // keeps the farm size it was planned for (intentForSize shifts it if the farm grew since).
  applyHerd(msg) {
    const tag = (i) => (msg.size ? { ...i, size: msg.size } : i);
    if (msg.type === 'herd_snapshot') {
      this._offset = clockOffset(msg.now, Date.now());
      this._intents = new Map(msg.intents.map((i) => [i.id, tag(i)]));
      this._streaming = true;
    } else {
      for (const i of msg.intents ?? []) this._intents.set(i.id, tag(i));
    }
  }

  playSound(msg) { if (msg.clip) audioEvents.emit(msg.clip); }       // 'moo' / 'cluck', quiet (eventMap gains)

  refresh(data) {
    this._data = data;
    if (!data?.farm || !data.catalog?.farm) return;
    // Footprints + draw depth of the farm's objects (as FarmObjectLayer sorts them: the front cell's
    // front corner), so an animal beside a building is drawn on the right side of it (animalDepth.mjs).
    const specs = data.catalog.farm.objects ?? {};
    this._boxes = (data.placements ?? []).filter((p) => p.scene === 'farm' && p.type !== 'fence' && specs[p.item] && Number.isFinite(p.x))
      .map((p) => {
        const sp = specs[p.item], f = frontCell(sp, p.x, p.y);
        return { x0: p.x, y0: p.y, x1: p.x + sp.w, y1: p.y + sp.h, depth: depthAt(cellCentre(f.x, f.y, data.farm.size).y + TILE_H / 2 + 1) };
      });
    const seen = new Set();
    for (const a of farmAnimals(data)) {
      seen.add(a.id);
      let it = this._items.get(a.id);
      if (!it) {
        const img = this.scene.add.image(0, 0, this._tex(a.type, 'idle')).setOrigin(0.5, 1);
        const bubble = this.scene.textures.exists('icons')
          ? this.scene.add.image(0, 0, 'icons', iconKey(BUBBLE[a.type])) : this.scene.add.circle(0, 0, BUBBLE_PX / 2, 0xffffff);
        bubble.setVisible(false);
        // Hungry (owner 2026-09-30: it should be visible): a bubble with the food it eats.
        const food = this._foodTex(a);
        const hunger = (food !== '__food_icon' ? this.scene.add.image(0, 0, food)
          : this.scene.textures.exists('icons') ? this.scene.add.image(0, 0, 'icons', iconKey('food'))
            : this.scene.add.circle(0, 0, FOOD_PX / 2, 0xf4d58d)).setVisible(false);
        this.world.objects.add([img, bubble, hunger]);
        it = { img, bubble, hunger, frameMs: 0, frame: 0, p: null };
        this._items.set(a.id, it);
      }
      it.a = a;
    }
    for (const [id, it] of this._items) if (!seen.has(id)) { it.img.destroy(); it.bubble.destroy(); it.hunger.destroy(); this._items.delete(id); }
  }

  // The food this animal eats (food_siano / food_ziarno, the shop's art), else the generic icon.
  _foodTex(a) {
    const food = this._data?.catalog?.farm?.animals?.[a.type]?.food_id;
    const key = food && `food_${food}`;
    return key && this.scene.textures.exists(key) ? key : '__food_icon';
  }

  /** The animal(s) under the pointer, whose menu is open, or living in the hovered hut glow
   *  (an id, a list of ids, or null to clear). */
  setHighlight(ids) {
    const want = new Set(ids == null ? [] : [].concat(ids));
    const key = [...want].sort().join('|');
    if (key === this._highlightKey) return;
    this._highlightKey = key;
    this._highlight = want.size === 1 ? [...want][0] : null;      // the single-animal ring fallback
    // A warm glow around each animal's own outline (WebGL): unmistakable even in a bunch.
    for (const [id, it] of this._items) {
      if (it.glow && !want.has(id)) { it.img.preFX?.remove(it.glow); it.glow = null; }
      if (!it.glow && want.has(id) && it.img.preFX) it.glow = it.img.preFX.addGlow(RING.line, 6, 0, false, 0.1, 12);
    }
    if (!this._ring) {
      this._ring = this.scene.add.ellipse(0, 0, 10, 10, RING.fill, RING.fillAlpha).setStrokeStyle(4, RING.line, 1).setVisible(false);
      this.world.objects.add(this._ring);
    }
    if (this._items.get(this._highlight)?.glow) this._ring.setVisible(false);
  }

  _tex(type, pose) {
    const key = `farm_animal_${type}_${pose}`;
    return this.scene.textures.exists(key) ? key : this._standIn(type);
  }

  // Until the walk sheets land: a white hen with a red comb, a white cow with black spots.
  _standIn(type) {
    const key = `farm_animal_ph_${type}`;
    if (this.scene.textures.exists(key)) return key;
    const [w, h] = STAND_IN[type] ?? [40, 34];
    const g = this.scene.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x000000, 0.18).fillEllipse(w / 2, h - 3, w * 0.8, 6);                 // shadow
    g.fillStyle(0xffffff, 1).fillEllipse(w / 2, h * 0.55, w * 0.8, h * 0.6);           // body
    if (type === 'krowa') {
      g.fillStyle(0x222222, 1).fillCircle(w * 0.38, h * 0.5, h * 0.12).fillCircle(w * 0.62, h * 0.62, h * 0.1);
      g.fillStyle(0xffffff, 1).fillCircle(w * 0.86, h * 0.36, h * 0.18);             // head
    } else {
      g.fillStyle(0xffffff, 1).fillCircle(w * 0.78, h * 0.3, h * 0.2);
      g.fillStyle(0xd62828, 1).fillCircle(w * 0.8, h * 0.1, h * 0.08);               // comb
      g.fillStyle(0xf4a300, 1).fillTriangle(w * 0.95, h * 0.3, w, h * 0.36, w * 0.93, h * 0.38);
    }
    g.generateTexture(key, w, h); g.destroy();
    return key;
  }

  update(dtMs) {
    const data = this._data;
    if (!data?.farm || !this._items.size) return;
    const now = Date.now() + this._offset;
    // Without the stream the local sky decides the night; with it the server's `inside` does.
    const localNight = !this._streaming && isNight(cycleT(clockInput()));
    const size = this.world.size;
    if (this._ring && !this._items.has(this._highlight)) this._ring.setVisible(false);
    if (this._ring && this._highlight && this._items.get(this._highlight) && !this._items.get(this._highlight).img.visible) this._ring.setVisible(false);
    for (const it of this._items.values()) {
      const streamed = this._intents.get(it.a.id);
      const intent = streamed ? intentForSize(streamed, size) : fallbackIntent(it.a, homeCell(data, it.a) ?? { x: 0, y: 0 });
      const p = positionAt(intent, now);
      it.p = p;
      const visible = p.visible && !localNight;
      it.img.setVisible(visible);
      const ready = (it.a.pending_count ?? 0) > 0;
      const hungry = isHungry(it.a, serverNow(), data.catalog?.farm?.animal_rules?.feed_hours);
      it.bubble.setVisible(visible && ready);
      it.hunger.setVisible(visible && hungry);
      if (this._ring && this._highlight === it.a.id && !it.glow) this._ring.setVisible(visible);
      if (!visible) continue;
      it.frameMs += dtMs;
      if (it.frameMs >= FRAME_MS) { it.frameMs = 0; it.frame = (it.frame + 1) % 4; }
      const still = restFrame(p.pose, p.since);                // head up (stand) or eating (idle); no stand art yet → idle
      const tex = this._tex(it.a.type, p.moving ? `walk${it.frame}`
        : still === 'stand' && this.scene.textures.exists(`farm_animal_${it.a.type}_stand`) ? 'stand' : 'idle');
      if (it.img.texture.key !== tex) it.img.setTexture(tex);
      if (tex.startsWith('farm_animal_ph_')) it.img.setDisplaySize(...(STAND_IN[it.a.type] ?? [40, 34]));
      else {                                          // real proportions against the character (animalRoam.animalScale)
        const walk0 = this.scene.textures.get(`farm_animal_${it.a.type}_walk0`).getSourceImage();
        const k = animalScale(it.a.type, walk0.height) ?? 0.5;
        const src = it.img.texture.getSourceImage();
        it.img.setDisplaySize(src.width * k, src.height * k);
      }
      it.img.setFlipX(!!p.faceLeft);
      const q = cellFToLocal(p.x, p.y, size);
      it.img.setPosition(q.x, q.y).setDepth(depthAmongObjects(depthAt(q.y + 1), p, this._boxes ?? []));
      // Both waiting: the product on the left, the food it wants on the right.
      const top = q.y - it.img.displayHeight - BUBBLE_PX / 2 - 2;
      const both = ready && hungry ? 14 : 0;
      it.bubble.setPosition(q.x - both, top).setDepth(it.img.depth + 1);
      if (it.bubble.setDisplaySize) it.bubble.setDisplaySize(BUBBLE_PX, BUBBLE_PX);
      if (hungry) {
        const want = this._foodTex(it.a);
        if (it.hunger.setTexture && want !== '__food_icon' && it.hunger.texture.key !== want) it.hunger.setTexture(want);   // the art landed
        it.hunger.setPosition(q.x + both, top - 1).setDepth(it.img.depth + 1);
        if (it.hunger.setDisplaySize) it.hunger.setDisplaySize(FOOD_PX, FOOD_PX);
      }
      if (this._ring && this._highlight === it.a.id && !it.glow) {
        const w = it.img.displayWidth * 0.62;
        this._ring.setPosition(q.x, q.y - 2).setSize(w, w * 0.36).setDepth(it.img.depth - 0.5);
        this._ring.geom?.setSize?.(w, w * 0.36); this._ring.updateDisplayOrigin?.();
      }
    }
  }

  // Screen point → the animal drawn there (its sprite bounds), front-most first.
  animalAtScreen(sx, sy) {
    const rect = (o) => { const b = o.getBounds(); return { x: b.x, y: b.y, w: b.width, h: b.height }; };
    const items = [...this._items.values()].filter((it) => it.img.visible).map((it) => ({
      it, depth: it.img.depth, body: rect(it.img), opaque: this._opaqueAt(it.img, sx, sy),
      bubble: it.bubble.visible ? rect(it.bubble) : it.hunger.visible ? rect(it.hunger) : null,
    }));
    return pickAnimal(items, sx, sy)?.it.a ?? null;
  }

  /** Petting (owner 2026-10-01): a few hearts float up from the animal and fade. */
  hearts(id, n = 4) {
    const it = this._items.get(id);
    if (!it?.img.visible || !this.scene.textures.exists('icons')) return;
    const top = it.img.y - it.img.displayHeight * 0.8;
    for (let i = 0; i < n; i++) {
      const x = it.img.x + (i - (n - 1) / 2) * 12 + Math.random() * 6 - 3;
      const h = this.scene.add.image(x, top, 'icons', iconKey('heart')).setDisplaySize(16, 16)
        .setDepth(it.img.depth + 2).setAlpha(0);
      this.world.objects.add(h);
      this.scene.tweens.add({
        targets: h, y: top - 34 - Math.random() * 14, x: x + (Math.random() * 16 - 8), alpha: { from: 1, to: 0 },
        scale: { from: h.scale * 0.6, to: h.scale * 1.15 }, delay: i * 110, duration: 900, ease: 'Sine.easeOut',
        onStart: () => h.setAlpha(1), onComplete: () => h.destroy(),
      });
    }
  }

  // Is the sprite actually drawn (not a transparent corner of its box) at this world point?
  // undefined when the point is outside the box or the texture can't be read (a stand-in).
  _opaqueAt(img, sx, sy) {
    const b = img.getBounds();
    if (!b.contains(sx, sy) || !img.frame) return undefined;
    let u = (sx - b.x) / b.width; const v = (sy - b.y) / b.height;
    if (img.flipX) u = 1 - u;
    const a = this.scene.textures.getPixelAlpha(Math.floor(u * img.frame.width), Math.floor(v * img.frame.height), img.texture.key, img.frame.name);
    return a == null ? undefined : a > 40;
  }

  /** A character's draw depth on the farm (owner 2026-10-01: Natalka standing in front of the Obora
   *  was painted behind it): plain feet-y sorting, corrected against the buildings around like an
   *  animal (animalDepth.mjs — in front once past a building's front walls). */
  characterDepth(x, feetY) {
    const p = localToCellF(x, feetY, this.world.size);
    return depthAmongObjects(depthAt(feetY), p, this._boxes ?? []);
  }

  // The animal's current cell (callers add 0.5 for the centre, as with 4c's walkers).
  cellOf(id) { const it = this._items.get(id); return it?.p ? { x: it.p.x - 0.5, y: it.p.y - 0.5 } : null; }
}
